import {
    event as logFullStoryEvent,
    identify as identifyFullStoryUser,
    getCurrentSessionURL
} from '@fullstory/browser'
import { setUser as setSentryUser } from '@sentry/browser'

import { isLocalhost, request } from './utils'
import { planTypes } from 'hooks'

import { PlaybookType, UserType } from 'app/types'

const noop = () => {}
const checkIsLocal = (func: any) => (isLocalhost ? noop : func)

// Identifies user in FullStory + Sentry + Mixpanel

type UserPropsType = {
    displayName: string
    email: string
    uid: string
    orgId: string | null
    env: string
    plan: keyof typeof planTypes
    browser: string
    browserVersion: string
    os: string
}

const registerUserInAnalytics = checkIsLocal((props: UserPropsType) => {
    try {
        const { displayName, email, uid, orgId, env, plan, browser, browserVersion, os } = props

        const appInfo = {
            source: 'web-application',
            environment: env,
            browser,
            browserVersion,
            os
        }

        const userInfo = {
            'user-id': uid,
            email,
            plan,
            name: displayName,
            'organization-id': orgId
        }

        // Fill in properties which we use every time when call backend to track any analytic event
        globalProperties = { ...appInfo }
        userProperties = { ...userInfo }

        // SENTRY
        setSentryUser({
            id: uid,
            username: displayName,
            ...appInfo,
            ...userInfo
        })

        // FULLSTORY
        identifyFullStoryUser(uid, { ...appInfo, ...userInfo })
    } catch (e) {
        console.error(e)
    }
})

// We use these properties in use-video-portal to enrich videoPlay event
export let userProperties = {}
export let globalProperties = {}

const trackEvent = async (event: string, props = {}) => {
    return request('/tk/v1/track', 'POST', {
        userProperties,
        globalProperties,
        eventData: {
            event,
            ...props
        }
    })
}
// Also logs an event to FullStory
const logToAnalytics = checkIsLocal((event: string, eventData = {}) => {
    const sessionURL = getCurrentSessionURL(true)

    try {
        const eventProps = { ...eventData, sessionURL, url: window.location.href }
        trackEvent(event, eventProps).catch(console.error)
        logFullStoryEvent(event, eventProps)
    } catch (e) {
        console.error(e)
    }
})

// UTILS
const playbookToAnalyticsProps = (
    playbook: PlaybookType & Partial<{ campaignId: string; utmCampaign: string }>,
    isAnonymous?: boolean
) => ({
    'pb-app': playbook.applications?.[0]?.applicationName || null,
    'pb-appId': playbook.applications?.[0]?.applicationId || null,
    'pb-apps': playbook.applications || null,
    'pb-tags': playbook.tags,
    'pb-id': playbook.id,
    'pb-title': playbook.title,
    'pb-visibility': playbook.visibility,
    'pb-creator-org-id': playbook.uploadedByOrgId,
    'pb-creator-name': playbook.username,
    'link-track-campaign-name': playbook.utmCampaign,
    'link-track-campaign-id': playbook.campaignId,
    playbookType: playbook.mode,
    ...(isAnonymous && {
        'anonymous-playbook': true,
        'organization-id': ''
    })
})

const userToAnalyticsProps = ({ displayName, email, uid }: UserType, prefix?: string) => ({
    [`${prefix ? `${prefix}-` : ''}user-display-name`]: displayName,
    [`${prefix ? `${prefix}-` : ''}user-email`]: email,
    [`${prefix ? `${prefix}-` : ''}user-uid`]: uid
})

const spaceToAnalyticsProps = ({ id, name, description, members, owners }: any = {}) => ({
    'space-id': id,
    'space-name': name,
    'space-description': description,
    'space-members': Object.fromEntries(
        Object.entries(members || {}).map(([id, userMember]) => [
            id,
            userToAnalyticsProps(<UserType>userMember)
        ])
    ),
    'space-owners': Object.fromEntries(
        Object.entries(owners || {}).map(([id, userOwner]) => [
            id,
            userToAnalyticsProps(<UserType>userOwner)
        ])
    )
})

export {
    logToAnalytics,
    registerUserInAnalytics,
    playbookToAnalyticsProps,
    spaceToAnalyticsProps,
    userToAnalyticsProps
}
